if (!customElements.get("quick-view-drawer")) {
  customElements.define(
    "quick-view-drawer",
    class QuickViewDrawer extends DrawerModal {
      constructor() {
        super();
        this.fetchedUrls = {};
        this.cssAssets = [
          "section-main-product.css",
          "component-price.css",
          "component-tooltip.css",
          "component-deferred-media.css",
        ];
        this.jsAssets = ["media-gallery.js", "recipient-form.js"];
        this.selector = "quickview-drawer";
      }

      connectedCallback() {
        super.connectedCallback?.();
        this.drawerContent = this.querySelector(".drawer-content-inner");
        this.drawerFooter = this.querySelector(".drawer-footer");
      }

      open(opener) {
        super.open(opener);
        const card = opener.closest(".card-product, .product-row");
        const jsProductLink = card.querySelector(".js-product-link");
        this.productUrl = jsProductLink.getAttribute("href");
        this.loadProduct(this.productUrl);
      }

      close() {
        super.close();
        this.drawerContent.innerHTML = '<span class="spinner spinner-lg"></span>';
        this.drawerFooter.innerHTML = "";
      }

      async loadProduct(productUrl) {
        if (this.fetchedUrls[productUrl]) {
          this.renderProduct(this.fetchedUrls[productUrl]);
          return;
        }

        try {
          let html;

          // 1. Try main-product section
          let response = await fetch(`${productUrl}?section_id=main-product`);
          let responseText = await response.text();
          html = new DOMParser().parseFromString(responseText, "text/html");

          if (!html.querySelector('[id^="ProductPage-template--"][id$="__main"]')) {
            // 2. Try product-template section
            response = await fetch(`${productUrl}?section_id=product-template`);
            responseText = await response.text();
            html = new DOMParser().parseFromString(responseText, "text/html");
          }

          if (!html.querySelector('[id^="ProductPage-template--"][id$="__main"]')) {
            // 3. Fallback: JSON endpoint
            const handle = productUrl.split("/products/")[1].split("?")[0];
            const jsonResp = await fetch(`/products/${handle}.js`);
            const productData = await jsonResp.json();
            this.renderJsonProduct(productData);
            return;
          }

          this.fetchedUrls[productUrl] = html;
          this.loadAssets(html);
          this.renderProduct(html);
        } catch (error) {
          console.error("Quick view load error:", error);
        }
      }

      renderProduct(html, variant) {
        const productPage = html.querySelector('[id^="ProductPage-template--"][id$="__main"]');
        if (!productPage) {
          console.error("Product section not found in quick view response.");
          return;
        }

        const sectionId = productPage.dataset.sectionId;
        productPage.innerHTML = productPage.innerHTML.replaceAll(sectionId, this.selector);

        if (this.querySelector("product-form") && variant) {
          this.handleUpdateProduct(productPage, sectionId, variant);
          return;
        }

        this.drawerContent.innerHTML = this.getQuickviewContentHtml(productPage, sectionId);
        this.drawerFooter.innerHTML = this.getProductFormHtml(productPage);
      }

      // 🔹 JSON-based fallback renderer with dynamic image + price
      renderJsonProduct(productData) {
        const { title, variants } = productData;

        // Build dropdown with price + image data
        const variantOptions = variants.map(
          (v) => `<option value="${v.id}" data-price="${v.price}" data-image="${v.featured_image?.src || ''}">
            ${v.title} - ${this.formatMoney(v.price, "INR")}
          </option>`
        ).join("");

        const firstVariant = variants[0];
        const firstImage = firstVariant.featured_image?.src || "";

        this.drawerContent.innerHTML = `
          <div class="product-quick-view-header">
            <div class="product-media-gallery-wrap">
              <div class="product-media-item">
                <img id="quickview-variant-image" src="${firstImage}" alt="${title}" class="product-media-image" style="max-width:100%">
              </div>
            </div>
            <div class="product-info-main">
              <h2 class="product-title">${title}</h2>
              <div class="product-block-price" id="quickview-variant-price">
                ${this.formatMoney(firstVariant.price, "INR")}
              </div>
            </div>
          </div>
        `;

        this.drawerFooter.innerHTML = `
          <div class="product-block-product-form">
            <form method="post" action="/cart/add" id="product-form-${this.selector}" class="product-form">
              <label for="variant-select">Choose an option:</label>
              <select id="variant-select" name="id" class="variant-select">
                ${variantOptions}
              </select>
              <button type="submit" name="add" class="button add-to-cart">Add to Cart</button>
            </form>
          </div>
        `;

        // ✅ Elements
        const form = this.drawerFooter.querySelector("form");
        const select = this.drawerFooter.querySelector("#variant-select");
        const imageEl = this.drawerContent.querySelector("#quickview-variant-image");
        const priceEl = this.drawerContent.querySelector("#quickview-variant-price");

        const updateVariant = (variantId, price, image) => {
          // Update hidden input
          form.querySelector("input[name='id']")?.remove();
          const hiddenInput = document.createElement("input");
          hiddenInput.type = "hidden";
          hiddenInput.name = "id";
          hiddenInput.value = variantId;
          form.appendChild(hiddenInput);

          // Update price
          if (price) {
            priceEl.textContent = this.formatMoney(price, "INR");
          }

          // Update image
          if (image) {
            imageEl.src = image;
          }
        };

        // Initialize with first variant
        updateVariant(firstVariant.id, firstVariant.price, firstImage);

        // Change handler for dropdown
        select.addEventListener("change", (e) => {
          const selectedOption = e.target.selectedOptions[0];
          const variantId = selectedOption.value;
          const variantPrice = selectedOption.dataset.price;
          const variantImage = selectedOption.dataset.image;
          updateVariant(variantId, variantPrice, variantImage);
        });
      }

      // ✅ Custom formatter to replace Shopify.formatMoney
      formatMoney(cents, currency = "INR") {
        if (typeof cents === "string") cents = cents.replace(".", "");
        let value = (cents / 100).toFixed(2);
        return new Intl.NumberFormat("en-IN", {
          style: "currency",
          currency,
        }).format(value);
      }

      handleUpdateProduct(html, sectionId, variant) {
        this.drawerContent.innerHTML = this.getQuickviewContentHtml(html, sectionId);
        this.updateProductForm(html);
        this.updateVariantInput(variant);
      }

      updateProductForm(html) {
        const productForm = document.getElementById(`product-form-${this.selector}`);
        if (productForm) {
          const addButtonDestination = productForm.querySelector('[name="add"]');
          const addButtonSource = html
            .querySelector(`#product-form-${this.selector}`)
            ?.querySelector('[name="add"]');

          if (addButtonSource && addButtonDestination) {
            addButtonDestination.outerHTML = addButtonSource.outerHTML;
          }

          productForm.style.opacity = 1;
        }
      }

      updateVariantInput(variant) {
        const productForms = this.querySelectorAll(
          `#product-form-${this.selector}, #product-form-installment`,
        );

        productForms.forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variant.id;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      getQuickviewContentHtml(html, sectionId) {
        return `
          <div class="product-quick-view-header">
            ${this.getProductMediaGalleryHtml(html)}
            ${this.getProductInfoHtml(html)}
          </div>
          ${this.getProductVariantsHtml(html, sectionId)}
          ${this.getProductPriceHtml(html)}
        `;
      }

      getProductMediaGalleryHtml(html) {
        const gallery = html.querySelector(".product-media-gallery-wrap");
        if (!gallery) return "";

        gallery.querySelector(".product-media-gallery-message")?.remove();
        gallery.querySelector(".product-media-scroll-wrap")?.remove();
        gallery.querySelector("carousel-navigation")?.remove();
        gallery.querySelector("product-modal")?.remove();
        gallery.querySelectorAll(".deferred-media-poster-button").forEach((b) => b.remove());
        gallery.querySelectorAll(".product-xr-button").forEach((b) => b.remove());

        gallery.querySelectorAll("img").forEach((img) => {
          try {
            const width = 100;
            const src = img.getAttribute("src").split("&width=")[0];
            const aspectRatio = img.width / img.height;
            const newSrc1x = `${src}&width=${width}`;
            const newSrc2x = `${src}&width=${width * 2}`;
            img.setAttribute("src", newSrc1x);
            img.setAttribute("srcset", `${newSrc1x} 1x, ${newSrc2x} 2x`);
            img.removeAttribute("sizes");
            img.setAttribute("width", width * 2);
            img.setAttribute("height", Math.floor((width * 2) / aspectRatio));
          } catch (error) {
            console.error(error);
          }
        });

        return gallery.outerHTML;
      }

      getProductInfoHtml(html) {
        const productInfo = html.querySelector(".product-info-main");
        if (!productInfo) return "";

        productInfo.querySelector(".product-info-meta-item.last")?.remove();
        productInfo.querySelector(".product-info-badges")?.remove();

        if (!productInfo.querySelector(".product-info-view-more")) {
          const fullDetailsButton = `
            <div class="product-info-view-more">
              <a href="${this.productUrl}" class="button-text-link">
                ${window.productsStrings.viewFullDetails}
                <span class="angle angle-right">
                  <span class="angle-icon"></span>
                </span>
              </a>
            </div>
          `;
          productInfo.insertAdjacentHTML("beforeend", fullDetailsButton);
        }

        return productInfo.outerHTML;
      }

      getProductVariantsHtml(html, sectionId) {
        const variants = html.querySelector(".product-variants");
        if (!variants) return "";
        const variantsElement =
          variants.querySelector("variant-radios") || variants.querySelector("variant-selects");
        if (variantsElement) {
          variantsElement.setAttribute("data-update-url", "false");
          variantsElement.setAttribute("data-section", sectionId);
        }
        return variants.outerHTML;
      }

      getProductPriceHtml(html) {
        const productPrice = html.querySelector(".product-block-price");
        return productPrice ? productPrice.outerHTML : "";
      }

      getProductFormHtml(html) {
        const productForm = html.querySelector(".product-block-product-form");
        if (!productForm) return "";
        const form = productForm.querySelector("form");
        if (form) {
          form.setAttribute("data-event-context", "QuickViewDrawer");
        }
        return productForm.outerHTML;
      }

      loadAssets(html) {
        this.cssAssets.forEach((asset) => {
          const links = html.querySelectorAll(`link[href*="${asset}"]`);
          links.forEach((link) => {
            const href = link.getAttribute("href");
            if (!document.querySelector(`link[href="${href}"]`)) {
              const newLink = document.createElement("link");
              newLink.href = href;
              newLink.rel = link.getAttribute("rel") || "stylesheet";
              newLink.type = link.getAttribute("type");
              newLink.media = link.getAttribute("media");
              document.head.appendChild(newLink);
            }
          });
        });

        this.jsAssets.forEach((asset) => {
          const scripts = html.querySelectorAll(`script[src*="${asset}"]`);
          scripts.forEach((script) => {
            const src = script.getAttribute("src");
            if (!document.querySelector(`script[src="${src}"]`)) {
              const newScript = document.createElement("script");
              newScript.src = src;
              if (script.hasAttribute("defer")) {
                newScript.defer = true;
              }
              document.head.appendChild(newScript);
            }
          });
        });
      }
    },
  );
}
